import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

export interface ReauthenticationResult {
  success: boolean;
  error?: string;
  requiresOAuth?: boolean;
}

export interface ReauthenticationOptions {
  user: User;
  session: Session;
  password?: string;
}

/**
 * Service for handling user reauthentication for sensitive operations like account deletion
 */
export class ReauthenticationService {
  /**
   * Reauthenticate a user based on their authentication method
   */
  static async reauthenticate(options: ReauthenticationOptions): Promise<ReauthenticationResult> {
    const { user, session, password } = options;

    try {
      // Check if user signed in with email/password
      if (this.isEmailPasswordUser(user)) {
        return await this.reauthenticateWithPassword(user.email!, password);
      }
      
      // Check if user signed in with OAuth (Sign in with Apple, Google, etc.)
      if (this.isOAuthUser(user)) {
        return await this.reauthenticateWithOAuth(user, session);
      }

      return {
        success: false,
        error: 'Unable to determine authentication method'
      };
    } catch (error) {
      console.error('Reauthentication error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Reauthentication failed'
      };
    }
  }

  /**
   * Reauthenticate user with email and password
   */
  private static async reauthenticateWithPassword(
    email: string, 
    password?: string
  ): Promise<ReauthenticationResult> {
    if (!password) {
      return {
        success: false,
        error: 'Password is required for reauthentication'
      };
    }

    try {
      // Attempt to sign in with the provided credentials
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return {
          success: false,
          error: 'Invalid password. Please try again.'
        };
      }

      if (data.user) {
        return { success: true };
      }

      return {
        success: false,
        error: 'Reauthentication failed'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Password verification failed'
      };
    }
  }

  /**
   * Reauthenticate user with OAuth provider
   */
  private static async reauthenticateWithOAuth(
    user: User, 
    session: Session
  ): Promise<ReauthenticationResult> {
    try {
      // For OAuth users, we verify the current session is still valid
      // and the tokens haven't been revoked
      const { data, error } = await supabase.auth.getUser();

      if (error || !data.user) {
        return {
          success: false,
          error: 'Session expired. Please sign in again.',
          requiresOAuth: true
        };
      }

      // Verify the user ID matches
      if (data.user.id !== user.id) {
        return {
          success: false,
          error: 'User session mismatch. Please sign in again.',
          requiresOAuth: true
        };
      }

      // For additional security with OAuth, we could implement:
      // 1. Token refresh to ensure tokens are still valid
      // 2. Provider-specific token validation
      // 3. Time-based session validation (e.g., require recent login)

      // Check if session is recent (within last 30 minutes for sensitive operations)
      const sessionAge = Date.now() - (session.expires_at ? session.expires_at * 1000 : 0);
      const maxSessionAge = 30 * 60 * 1000; // 30 minutes

      if (sessionAge > maxSessionAge) {
        return {
          success: false,
          error: 'Session too old. Please sign in again for security.',
          requiresOAuth: true
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'OAuth verification failed',
        requiresOAuth: true
      };
    }
  }

  /**
   * Check if user signed in with email/password
   */
  private static isEmailPasswordUser(user: User): boolean {
    // Check if user has email and no OAuth provider identities
    return !!(
      user.email && 
      (!user.app_metadata?.providers || user.app_metadata.providers.includes('email'))
    );
  }

  /**
   * Check if user signed in with OAuth provider
   */
  private static isOAuthUser(user: User): boolean {
    // Check for OAuth provider in app metadata
    const providers = user.app_metadata?.providers || [];
    const oauthProviders = ['apple', 'google', 'github', 'facebook', 'twitter'];
    
    return providers.some((provider: string) => oauthProviders.includes(provider));
  }

  /**
   * Get the OAuth provider for the user
   */
  static getOAuthProvider(user: User): string | null {
    const providers = user.app_metadata?.providers || [];
    const oauthProviders = ['apple', 'google', 'github', 'facebook', 'twitter'];
    
    const provider = providers.find((p: string) => oauthProviders.includes(p));
    return provider || null;
  }

  /**
   * Initiate OAuth reauthentication flow
   */
  static async initiateOAuthReauth(provider: string): Promise<ReauthenticationResult> {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider as 'apple' | 'google' | 'github' | 'facebook' | 'twitter',
        options: {
          redirectTo: `${window.location.origin}/auth/callback?reauth=true`,
          queryParams: {
            prompt: 'login' // Force login prompt
          }
        }
      });

      if (error) {
        return {
          success: false,
          error: `Failed to initiate ${provider} reauthentication`
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'OAuth reauthentication failed'
      };
    }
  }

  /**
   * Validate that reauthentication is required for the current operation
   */
  static shouldRequireReauth(_session: Session): boolean {
    // Always require reauthentication for account deletion
    // Could be made configurable based on operation sensitivity
    return true;
  }

  /**
   * Get user-friendly authentication method description
   */
  static getAuthMethodDescription(user: User): string {
    if (this.isEmailPasswordUser(user)) {
      return 'email and password';
    }
    
    const provider = this.getOAuthProvider(user);
    if (provider) {
      return `Sign in with ${provider.charAt(0).toUpperCase() + provider.slice(1)}`;
    }
    
    return 'your account credentials';
  }
}
